import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { FileText, CheckCircle2, Clock, Wallet, Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

const PIE_COLORS = ["#4E9BEE", "#15803D", "#B91C1C", "#64748B", "#0EA5E9"];

export default function Reports() {
  const [summary, setSummary] = useState(null);
  const [dynamics, setDynamics] = useState([]);
  const [byType, setByType] = useState([]);
  const [top, setTop] = useState([]);
  const [counterparties, setCounterparties] = useState([]);
  const [filters, setFilters] = useState({ start_date: "", end_date: "", counterparty_id: "" });

  useEffect(() => {
    api.get("/counterparties").then(({ data }) => setCounterparties(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const params = {};
    if (filters.start_date) params.start_date = filters.start_date;
    if (filters.end_date) params.end_date = filters.end_date;
    if (filters.counterparty_id) params.counterparty_id = filters.counterparty_id;
    api.get("/reports/summary", { params }).then(({ data }) => setSummary(data));
    api.get("/reports/dynamics", { params }).then(({ data }) => setDynamics(data));
    api.get("/reports/by-type", { params }).then(({ data }) => setByType(data));
    api.get("/reports/top-counterparties", { params: { start_date: filters.start_date, end_date: filters.end_date } }).then(({ data }) => setTop(data));
  }, [filters]);

  const buildParams = () => {
    const params = new URLSearchParams();
    if (filters.start_date) params.set("start_date", filters.start_date);
    if (filters.end_date) params.set("end_date", filters.end_date);
    if (filters.counterparty_id) params.set("counterparty_id", filters.counterparty_id);
    return params;
  };

  const handleExport = () => window.open(`${API}/reports/export?${buildParams().toString()}`, "_blank");
  const handleExportPdf = () => window.open(`${API}/reports/export-pdf?${buildParams().toString()}`, "_blank");

  const kpis = summary
    ? [
        { label: "Документов за период", value: summary.total_documents, icon: FileText, color: "text-slate-900" },
        { label: "Подписано", value: `${summary.signed_percent}%`, icon: CheckCircle2, color: "text-blue-600" },
        { label: "Среднее время подписания", value: `${summary.avg_signing_hours} ч`, icon: Clock, color: "text-emerald-600" },
        { label: "Сэкономлено на бумаге и курьерах", value: `${summary.estimated_savings_rub?.toLocaleString("ru-RU")} ₽`, icon: Wallet, color: "text-amber-600" },
      ]
    : [];

  return (
    <div data-testid="reports-page">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <h1 className="text-2xl font-bold text-slate-900">Отчёты и аналитика</h1>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-report-button">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Экспорт в Excel
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportPdf} data-testid="export-report-pdf-button">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Экспорт в PDF
          </Button>
        </div>
      </div>
      <p className="text-sm text-slate-500 mb-4">Статистика документооборота — то, чего нет в классическом Диадоке</p>

      <div className="flex flex-wrap items-end gap-3 mb-6 bg-white border border-slate-200 rounded-xl p-3">
        <div className="space-y-1">
          <Label className="text-xs">С даты</Label>
          <Input type="date" data-testid="reports-start-date-input" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">По дату</Label>
          <Input type="date" data-testid="reports-end-date-input" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} />
        </div>
        <div className="space-y-1 w-full sm:w-auto min-w-0">
          <Label className="text-xs">Контрагент</Label>
          <select
            data-testid="reports-counterparty-select"
            value={filters.counterparty_id}
            onChange={(e) => setFilters({ ...filters, counterparty_id: e.target.value })}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white h-9 w-full max-w-full sm:w-auto"
          >
            <option value="">Все контрагенты</option>
            {counterparties.map((cp) => (
              <option key={cp.id} value={cp.id}>{cp.name}</option>
            ))}
          </select>
        </div>
        {(filters.start_date || filters.end_date || filters.counterparty_id) && (
          <Button variant="ghost" size="sm" onClick={() => setFilters({ start_date: "", end_date: "", counterparty_id: "" })} data-testid="reports-clear-filters-button">
            Сбросить
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6" data-testid="analytics-kpi-summary">
        {kpis.map((k, i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-xl p-4">
            <k.icon className={`w-5 h-5 mb-2 ${k.color}`} />
            <div className="text-xl font-bold text-slate-900">{k.value}</div>
            <div className="text-xs text-slate-500 mt-0.5">{k.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-4" data-testid="dynamics-chart">
          <div className="text-sm font-semibold text-slate-700 mb-3">Динамика документооборота</div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={dynamics}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="signed" stroke="#4E9BEE" strokeWidth={2} name="Подписано" dot={false} />
              <Line type="monotone" dataKey="in_progress" stroke="#15803D" strokeWidth={2} name="Отправлено на подпись" dot={false} />
              <Line type="monotone" dataKey="rejected" stroke="#B91C1C" strokeWidth={2} name="Отказано" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="by-type-chart">
          <div className="text-sm font-semibold text-slate-700 mb-3">По типам документов</div>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={byType} dataKey="count" nameKey="type" cx="50%" cy="50%" outerRadius={80} label={({ type }) => type}>
                {byType.map((_, i) => (
                  <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="top-counterparties-leaderboard">
        <div className="text-sm font-semibold text-slate-700 mb-3">Рейтинг контрагентов по скорости подписания</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-slate-400 border-b border-slate-100">
              <th className="py-2">Контрагент</th>
              <th className="py-2">Документов</th>
              <th className="py-2">Подписано</th>
              <th className="py-2">Среднее время, ч</th>
            </tr>
          </thead>
          <tbody>
            {top.map((t, i) => (
              <tr key={i} className="border-b border-slate-50">
                <td className="py-2 font-medium text-slate-800">{t.name}</td>
                <td className="py-2 text-slate-600">{t.total_documents}</td>
                <td className="py-2 text-slate-600">{t.signed}</td>
                <td className="py-2 text-slate-600">{t.avg_signing_hours ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {top.length === 0 && <div className="text-center text-slate-400 text-sm py-10">Пока нет данных для рейтинга</div>}
      </div>
    </div>
  );
}
